/**
 * HoMessagesTab - Messages tab for active project detail.
 * 1:1 direct messaging with project participants.
 * Styled with homeowner design primitives (FloatPanel, Eyebrow, Avatar, Icn, Pill).
 */

import type {
	ProjectDirectMessageDto,
	ProjectDirectThreadDto,
	SendProjectDirectMessageAttachmentInput,
} from "@shared/types";
import * as React from "react";
import {
	createOrGetProjectDirectThread,
	getProjectDirectMessages,
	getProjectDirectThreads,
	markProjectDirectThreadRead,
	sendProjectDirectMessage,
} from "../../../services/project-direct-messages-api.service";
import { connectProjectDirectMessagesRealtime } from "../../../services/project-direct-messages-realtime.service";
import { uploadMultipleFiles } from "../../../services/projects-api.service";
import { Avatar, Eyebrow, FloatPanel, Icn, Pill } from "../design-primitives";

interface HoMessagesTabProps {
	projectId: string;
	defaultThreadUserId?: string | null;
}

function roleLabel(role: ProjectDirectThreadDto["partner"]["role"]): string {
	switch (role) {
		case "homeowner":
			return "Homeowner";
		case "designer":
			return "Designer";
		case "contractor":
			return "Contractor";
		default:
			return role;
	}
}

function initials(name: string): string {
	return name
		.split(" ")
		.map((part) => part[0])
		.join("")
		.slice(0, 2)
		.toUpperCase();
}

function formatTimestamp(value: string): string {
	return new Date(value).toLocaleTimeString([], {
		hour: "numeric",
		minute: "2-digit",
	});
}

function formatDateDivider(dateStr: string): string {
	const date = new Date(dateStr);
	const today = new Date();
	const yesterday = new Date(today);
	yesterday.setDate(yesterday.getDate() - 1);

	if (date.toDateString() === today.toDateString()) return "Today";
	if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
	return date.toLocaleDateString([], { month: "short", day: "numeric" });
}

function shouldShowDateDivider(
	prevMessage: ProjectDirectMessageDto | null,
	currentMessage: ProjectDirectMessageDto,
): boolean {
	if (!prevMessage) return true;
	const prevDate = new Date(prevMessage.createdAt).toDateString();
	const currDate = new Date(currentMessage.createdAt).toDateString();
	return prevDate !== currDate;
}

function getAvatarTone(
	role: ProjectDirectThreadDto["partner"]["role"],
): "ink" | "terra" | "stone" | "cognac" | "amber" {
	switch (role) {
		case "designer":
			return "ink";
		case "homeowner":
			return "terra";
		case "contractor":
			return "stone";
		default:
			return "amber";
	}
}

export function HoMessagesTab({
	projectId,
	defaultThreadUserId,
}: HoMessagesTabProps) {
	const [threads, setThreads] = React.useState<ProjectDirectThreadDto[]>([]);
	const [selectedThread, setSelectedThread] =
		React.useState<ProjectDirectThreadDto | null>(null);
	const [messages, setMessages] = React.useState<ProjectDirectMessageDto[]>([]);
	const [isLoadingThreads, setIsLoadingThreads] = React.useState(true);
	const [isLoadingMessages, setIsLoadingMessages] = React.useState(false);
	const [draft, setDraft] = React.useState("");
	const [sendError, setSendError] = React.useState<string | null>(null);
	const [attachmentFiles, setAttachmentFiles] = React.useState<File[]>([]);
	const [uploadedAttachments, setUploadedAttachments] = React.useState<
		SendProjectDirectMessageAttachmentInput[]
	>([]);
	const [isUploading, setIsUploading] = React.useState(false);
	const [isSending, setIsSending] = React.useState(false);
	const messagesEndRef = React.useRef<HTMLDivElement>(null);
	const mountedRef = React.useRef(true);
	const openThreadAbortRef = React.useRef(0);
	// Track which defaultThreadUserId we've already handled to avoid
	// re-opening the same thread every time the threads list refreshes.
	const handledThreadUserIdRef = React.useRef<string | null>(null);

	const loadThreads = React.useCallback(async (): Promise<void> => {
		setIsLoadingThreads(true);
		try {
			const nextThreads = await getProjectDirectThreads(projectId);
			setThreads(nextThreads);
		} finally {
			setIsLoadingThreads(false);
		}
	}, [projectId]);

	React.useEffect(() => {
		void loadThreads();
	}, [loadThreads]);

	React.useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
		};
	}, []);

	const openThread = React.useCallback(
		async (thread: ProjectDirectThreadDto): Promise<void> => {
			const callId = ++openThreadAbortRef.current;
			setSendError(null);
			setIsLoadingMessages(true);
			try {
				const ensuredThread =
					thread.id == null
						? await createOrGetProjectDirectThread(projectId, {
								participantUserId: thread.partner.userId,
							})
						: thread;
				if (callId !== openThreadAbortRef.current || !mountedRef.current)
					return;
				const page = await getProjectDirectMessages(
					projectId,
					ensuredThread.id as string,
				);
				if (callId !== openThreadAbortRef.current || !mountedRef.current)
					return;
				setSelectedThread(page.thread);
				setMessages(page.messages);
				setThreads((current) => {
					const updatedThreads = current.map((item) =>
						item.partner.userId === page.thread.partner.userId
							? page.thread
							: item,
					);
					return updatedThreads;
				});
				const lastMessage = page.messages[page.messages.length - 1];
				if (
					lastMessage &&
					callId === openThreadAbortRef.current &&
					mountedRef.current
				) {
					await markProjectDirectThreadRead(
						projectId,
						page.thread.id as string,
						{
							lastReadMessageId: lastMessage.id,
						},
					);
				}
			} finally {
				if (callId === openThreadAbortRef.current) {
					setIsLoadingMessages(false);
				}
			}
		},
		[projectId],
	);

	React.useEffect(() => {
		// Reset handled ref whenever defaultThreadUserId changes so a new
		// userId (e.g. clicking a different person on the Teams tab) is processed.
		if (defaultThreadUserId !== handledThreadUserIdRef.current) {
			handledThreadUserIdRef.current = null;
		}
	}, [defaultThreadUserId]);

	React.useEffect(() => {
		if (!defaultThreadUserId || threads.length === 0) return;
		// Skip if we already opened this thread in a previous render
		if (handledThreadUserIdRef.current === defaultThreadUserId) return;
		const targetThread = threads.find(
			(t) => t.partner.userId === defaultThreadUserId,
		);
		if (targetThread) {
			handledThreadUserIdRef.current = defaultThreadUserId;
			void openThread(targetThread);
		}
	}, [defaultThreadUserId, threads, openThread]);

	React.useEffect(() => {
		if (!selectedThread) return;
		if (typeof messagesEndRef.current?.scrollIntoView === "function") {
			messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
		}
	}, [selectedThread]);

	React.useEffect(() => {
		let connection: { disconnect: () => void } | null = null;
		let cancelled = false;
		void connectProjectDirectMessagesRealtime({
			projectId,
			onEvent: async (event: { projectId: string; threadId: string }) => {
				if (cancelled || event.projectId !== projectId) return;
				await loadThreads();
				if (!mountedRef.current) return;
				const currentSelectedId = selectedThread?.id;
				if (currentSelectedId && event.threadId === currentSelectedId) {
					const nextPage = await getProjectDirectMessages(
						projectId,
						currentSelectedId,
					);
					if (!mountedRef.current) return;
					setSelectedThread(nextPage.thread);
					setMessages(nextPage.messages);
				}
			},
			onError: () => undefined,
		})
			.then((value) => {
				connection = value;
			})
			.catch(() => undefined);
		return () => {
			cancelled = true;
			connection?.disconnect();
		};
	}, [projectId, selectedThread?.id, loadThreads]);

	async function handleAttachmentChange(files: FileList | null): Promise<void> {
		const nextFiles = files ? Array.from(files) : [];
		if (nextFiles.length === 0) return;
		setIsUploading(true);
		setSendError(null);
		try {
			const uploadResult = await uploadMultipleFiles(nextFiles, {
				folder: "messages",
				projectId,
			});
			if (!uploadResult.success || !uploadResult.urls) {
				setSendError(uploadResult.error || "Failed to upload files");
				return;
			}
			const urls = uploadResult.urls;
			setAttachmentFiles((current) => [...current, ...nextFiles]);
			setUploadedAttachments((current) => [
				...current,
				...urls.map((url: string, index: number) => ({
					fileUrl: url,
					fileName: nextFiles[index]?.name,
					mimeType: nextFiles[index]?.type || "image/*",
					fileSize: nextFiles[index]?.size,
				})),
			]);
		} finally {
			setIsUploading(false);
		}
	}

	async function handleSend(): Promise<void> {
		if (!selectedThread?.id) return;
		if (!draft.trim() && uploadedAttachments.length === 0) return;
		const sendingThreadId = selectedThread.id;
		const sendingPartnerId = selectedThread.partner.userId;
		setSendError(null);
		setIsSending(true);
		const optimisticMessage: ProjectDirectMessageDto = {
			id: `optimistic-${Date.now()}`,
			threadId: sendingThreadId,
			projectId,
			sender: { userId: "me", role: "homeowner", name: "You" },
			content: draft.trim() || null,
			messageType:
				draft.trim() && uploadedAttachments.length > 0
					? "mixed"
					: uploadedAttachments.length > 0
						? "attachment"
						: "text",
			attachments: uploadedAttachments.map((attachment, index) => ({
				id: `upload-${index}`,
				fileUrl: attachment.fileUrl,
				fileName: attachment.fileName || null,
				mimeType: attachment.mimeType,
				fileSize: attachment.fileSize || null,
			})),
			createdAt: new Date().toISOString(),
			isOwnMessage: true,
		};
		setMessages((current) => [...current, optimisticMessage]);
		const result = await sendProjectDirectMessage(projectId, sendingThreadId, {
			content: draft.trim(),
			attachments: uploadedAttachments,
		});
		setIsSending(false);
		if (!mountedRef.current || selectedThread.id !== sendingThreadId) {
			return;
		}
		if (!result.success || !result.message) {
			setMessages((current) =>
				current.filter((message) => message.id !== optimisticMessage.id),
			);
			setSendError(result.error || "Failed to send direct message");
			return;
		}
		const sentMessage = result.message;
		setMessages((current) =>
			current.map((message) =>
				message.id === optimisticMessage.id ? sentMessage : message,
			),
		);
		setThreads((current) => {
			return current.map((thread) => {
				if (thread.partner.userId !== sendingPartnerId) {
					return thread;
				}
				return {
					...thread,
					id: sendingThreadId,
					lastMessage: {
						id: result.message?.id,
						content: result.message?.content,
						messageType: result.message?.messageType,
						createdAt: result.message?.createdAt,
						senderUserId: result.message?.sender.userId,
						attachmentCount: result.message?.attachments.length,
					},
					updatedAt: result.message?.createdAt,
				} as ProjectDirectThreadDto;
			});
		});
		setDraft("");
		setAttachmentFiles([]);
		setUploadedAttachments([]);
	}

	function handleKeyDown(event: React.KeyboardEvent): void {
		if (event.key === "Enter" && !event.shiftKey) {
			event.preventDefault();
			void handleSend();
		}
	}

	return (
		<div>
			{/* Header */}
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "flex-end",
					marginBottom: 18,
					flexWrap: "wrap",
					gap: 12,
				}}
			>
				<div>
					<Eyebrow>PROJECT CHAT</Eyebrow>
					<h2
						style={{
							fontFamily: "var(--font-display)",
							fontWeight: 500,
							fontSize: 30,
							letterSpacing: "-0.02em",
							color: "var(--color-dashboard-text)",
							margin: "8px 0 0",
						}}
					>
						Messages
					</h2>
				</div>
			</div>

			{/* Messages Panel */}
			<FloatPanel elev={2} padding={0} style={{ overflow: "hidden" }}>
				<div
					style={{
						display: "grid",
						gridTemplateColumns: "300px 1fr",
						height: 580,
					}}
				>
					{/* Thread List */}
					<div
						style={{
							borderRight: "1px solid var(--color-dashboard-border)",
							display: "flex",
							flexDirection: "column",
							background: "var(--color-dashboard-paper)",
						}}
					>
						<div
							style={{
								padding: "20px 20px 16px",
								borderBottom: "1px solid var(--color-dashboard-border)",
								background: "var(--color-dashboard-surface)",
							}}
						>
							<div
								style={{
									fontFamily: "var(--font-mono)",
									fontSize: 10,
									fontWeight: 600,
									letterSpacing: "0.18em",
									textTransform: "uppercase",
									color: "var(--color-dashboard-accent-dark)",
								}}
							>
								<Icn
									name="sparkles"
									size={12}
									color="var(--color-dashboard-accent-dark)"
								/>{" "}
								Private coordination
							</div>
							<p
								style={{
									fontFamily: "var(--font-body)",
									fontSize: 12,
									color: "var(--color-dashboard-muted)",
									marginTop: 4,
								}}
							>
								Start a 1:1 thread with anyone on this project.
							</p>
						</div>

						<div style={{ flex: 1, overflowY: "auto", padding: 12 }}>
							{isLoadingThreads ? (
								<div
									style={{ display: "flex", flexDirection: "column", gap: 12 }}
								>
									{["msg-skel-1", "msg-skel-2", "msg-skel-3", "msg-skel-4"].map(
										(key) => (
											<div
												key={key}
												style={{
													display: "flex",
													gap: 12,
													padding: 14,
													borderRadius: 11,
													border: "1px solid var(--color-dashboard-border)",
													background: "var(--color-dashboard-paper)",
												}}
											>
												<div
													style={{
														width: 44,
														height: 44,
														borderRadius: "50%",
														background: "var(--color-dashboard-border)",
													}}
												/>
												<div style={{ flex: 1 }}>
													<div
														style={{
															width: "60%",
															height: 14,
															borderRadius: 3,
															background: "var(--color-dashboard-border)",
															marginBottom: 8,
														}}
													/>
													<div
														style={{
															width: "80%",
															height: 10,
															borderRadius: 3,
															background: "var(--color-dashboard-border)",
														}}
													/>
												</div>
											</div>
										),
									)}
								</div>
							) : threads.length === 0 ? (
								<div
									style={{
										textAlign: "center",
										padding: "40px 20px",
										color: "var(--color-dashboard-muted)",
									}}
								>
									<p
										style={{
											fontFamily: "var(--font-body)",
											fontSize: 14,
										}}
									>
										No conversations yet.
									</p>
									<p
										style={{
											fontFamily: "var(--font-body)",
											fontSize: 12,
											marginTop: 8,
										}}
									>
										Select a participant to start messaging.
									</p>
								</div>
							) : (
								<div
									style={{ display: "flex", flexDirection: "column", gap: 8 }}
								>
									{threads.map((thread) => {
										const isSelected =
											selectedThread?.partner.userId === thread.partner.userId;
										const tone = getAvatarTone(thread.partner.role);
										return (
											<button
												type="button"
												key={thread.partner.userId}
												onClick={() => void openThread(thread)}
												style={{
													display: "flex",
													alignItems: "flex-start",
													gap: 12,
													padding: 14,
													borderRadius: 14,
													border: isSelected
														? "2px solid var(--color-dashboard-accent)"
														: "1px solid var(--color-dashboard-border)",
													background: isSelected
														? "var(--color-dashboard-paper)"
														: "transparent",
													cursor: "pointer",
													textAlign: "left",
													width: "100%",
													boxShadow: isSelected
														? "0 4px 16px -4px color-mix(in srgb, var(--color-dashboard-accent) 25%, transparent)"
														: "none",
													transition: "all 200ms ease",
												}}
											>
												<Avatar
													initials={initials(thread.partner.name)}
													tone={tone}
													size={44}
												/>
												<div style={{ flex: 1, minWidth: 0 }}>
													<div
														style={{
															display: "flex",
															justifyContent: "space-between",
															alignItems: "center",
														}}
													>
														<span
															style={{
																fontFamily: "var(--font-body)",
																fontWeight: 600,
																fontSize: 14,
																color: "var(--color-dashboard-text)",
															}}
														>
															{thread.partner.name}
														</span>
														{thread.unreadCount > 0 && (
															<span
																style={{
																	minWidth: 20,
																	height: 20,
																	borderRadius: 10,
																	background: "var(--color-dashboard-accent)",
																	color: "var(--color-dashboard-on-fill)",
																	fontSize: 11,
																	fontWeight: 700,
																	display: "flex",
																	alignItems: "center",
																	justifyContent: "center",
																	padding: "0 6px",
																}}
															>
																{thread.unreadCount}
															</span>
														)}
													</div>
													<div style={{ marginTop: 2 }}>
														<span
															style={{
																fontFamily: "var(--font-mono)",
																fontSize: 9.5,
																fontWeight: 600,
																letterSpacing: "0.15em",
																textTransform: "uppercase",
																color: "var(--color-dashboard-muted)",
															}}
														>
															{roleLabel(thread.partner.role)}
														</span>
													</div>
													<p
														style={{
															fontFamily: "var(--font-body)",
															fontSize: 12.5,
															color: "var(--color-dashboard-muted)",
															marginTop: 6,
															overflow: "hidden",
															textOverflow: "ellipsis",
															whiteSpace: "nowrap",
														}}
													>
														{thread.lastMessage?.messageType === "attachment"
															? "📎 Sent an image"
															: thread.lastMessage?.content ||
																"No messages yet"}
													</p>
												</div>
											</button>
										);
									})}
								</div>
							)}
						</div>
					</div>

					{/* Chat Area */}
					{selectedThread ? (
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								background:
									"radial-gradient(circle at top, color-mix(in srgb, var(--color-dashboard-warm-100) 50%, transparent), transparent 40%), linear-gradient(180deg, var(--color-dashboard-paper) 0%, var(--color-dashboard-surface) 100%)",
							}}
						>
							{/* Chat Header */}
							<div
								style={{
									padding: "16px 24px",
									borderBottom: "1px solid var(--color-dashboard-border)",
									background:
										"color-mix(in srgb, var(--color-dashboard-paper) 80%, transparent)",
									display: "flex",
									alignItems: "center",
									gap: 12,
								}}
							>
								<Avatar
									initials={initials(selectedThread.partner.name)}
									tone={getAvatarTone(selectedThread.partner.role)}
									size={44}
								/>
								<div>
									<p
										style={{
											fontFamily: "var(--font-body)",
											fontWeight: 600,
											fontSize: 15,
											color: "var(--color-dashboard-text)",
										}}
									>
										{selectedThread.partner.name}
									</p>
									<Pill
										tone="neutral"
										dot={false}
										style={{ height: 18, fontSize: 9.5 }}
									>
										{roleLabel(selectedThread.partner.role)}
									</Pill>
								</div>
							</div>

							{/* Messages */}
							<div
								style={{
									flex: "1 1 0%",
									minHeight: 0,
									overflowY: "auto",
									padding: "24px 28px 8px",
									display: "flex",
									flexDirection: "column",
									gap: 16,
								}}
							>
								{isLoadingMessages ? (
									<div
										style={{
											display: "flex",
											flexDirection: "column",
											gap: 16,
											alignItems: "flex-start",
										}}
									>
										{[
											"chat-skel-1",
											"chat-skel-2",
											"chat-skel-3",
											"chat-skel-4",
										].map((key, idx) => (
											<div
												key={key}
												style={{
													display: "flex",
													gap: 10,
													alignItems: "flex-end",
													flexDirection: idx % 2 === 0 ? "row" : "row-reverse",
												}}
											>
												<div
													style={{
														width: 32,
														height: 32,
														borderRadius: "50%",
														background: "var(--color-dashboard-border)",
													}}
												/>
												<div
													style={{
														width: 200,
														height: 60,
														borderRadius: 13,
														background: "var(--color-dashboard-border)",
													}}
												/>
											</div>
										))}
									</div>
								) : messages.length === 0 ? (
									<div
										style={{
											flex: 1,
											display: "flex",
											flexDirection: "column",
											alignItems: "center",
											justifyContent: "center",
											textAlign: "center",
											padding: 40,
										}}
									>
										<div
											style={{
												width: 64,
												height: 64,
												borderRadius: "50%",
												background: "var(--color-dashboard-divider)",
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
											}}
										>
											<Icn
												name="message"
												size={28}
												color="var(--color-dashboard-accent-dark)"
											/>
										</div>
										<h4
											style={{
												fontFamily: "var(--font-display)",
												fontStyle: "italic",
												fontWeight: 500,
												fontSize: 20,
												color: "var(--color-dashboard-text)",
												marginTop: 16,
											}}
										>
											Start the first private update
										</h4>
										<p
											style={{
												fontFamily: "var(--font-body)",
												fontSize: 13,
												color: "var(--color-dashboard-muted)",
												marginTop: 8,
												maxWidth: 280,
											}}
										>
											Use this thread for direct coordination with{" "}
											{selectedThread.partner.name}. Comments and project
											activity stay separate.
										</p>
									</div>
								) : (
									<>
										{messages.map((message, index) => {
											const prevMessage =
												index > 0 ? messages[index - 1] : null;
											const showDateDivider = shouldShowDateDivider(
												prevMessage,
												message,
											);
											const tone = getAvatarTone(message.sender.role);
											return (
												<React.Fragment key={message.id}>
													{showDateDivider && (
														<div
															style={{
																display: "flex",
																alignItems: "center",
																gap: 12,
																margin: "8px 0",
															}}
														>
															<div
																style={{
																	flex: 1,
																	height: 1,
																	background: "var(--color-dashboard-border)",
																}}
															/>
															<span
																style={{
																	fontFamily: "var(--font-mono)",
																	fontSize: 10,
																	color: "var(--color-dashboard-muted)",
																}}
															>
																{formatDateDivider(message.createdAt)}
															</span>
															<div
																style={{
																	flex: 1,
																	height: 1,
																	background: "var(--color-dashboard-border)",
																}}
															/>
														</div>
													)}
													<div
														style={{
															display: "flex",
															flexDirection: message.isOwnMessage
																? "row-reverse"
																: "row",
															alignItems: "flex-end",
															gap: 10,
															maxWidth: 540,
															alignSelf: message.isOwnMessage
																? "flex-end"
																: "flex-start",
														}}
													>
														<Avatar
															initials={initials(message.sender.name)}
															tone={tone}
															size={32}
														/>
														<div>
															<div
																style={{
																	display: "flex",
																	alignItems: "center",
																	gap: 8,
																	marginBottom: 4,
																	flexDirection: message.isOwnMessage
																		? "row-reverse"
																		: "row",
																}}
															>
																<span
																	style={{
																		fontFamily: "var(--font-body)",
																		fontWeight: 600,
																		fontSize: 12,
																		color: "var(--color-dashboard-text)",
																	}}
																>
																	{message.isOwnMessage
																		? "You"
																		: message.sender.name}
																</span>
																<span
																	style={{
																		fontFamily: "var(--font-mono)",
																		fontSize: 9.5,
																		color: "var(--color-dashboard-placeholder)",
																	}}
																>
																	{formatTimestamp(message.createdAt)}
																</span>
															</div>
															<div
																style={{
																	padding: "12px 16px",
																	borderRadius: message.isOwnMessage
																		? "18px 18px 6px 18px"
																		: "18px 18px 18px 6px",
																	background: message.isOwnMessage
																		? "var(--color-dashboard-accent)"
																		: "var(--color-dashboard-paper)",
																	border: message.isOwnMessage
																		? "none"
																		: "1px solid var(--color-dashboard-border)",
																	color: message.isOwnMessage
																		? "var(--color-dashboard-on-fill)"
																		: "var(--color-dashboard-text)",
																	fontFamily: "var(--font-body)",
																	fontSize: 14,
																	lineHeight: 1.5,
																	boxShadow:
																		"0 1px 2px color-mix(in srgb, var(--color-dashboard-shadow-ink) 6%, transparent)",
																}}
															>
																{message.content && (
																	<p style={{ margin: 0 }}>{message.content}</p>
																)}
																{message.attachments.length > 0 && (
																	<div
																		style={{
																			display: "grid",
																			gridTemplateColumns: "repeat(2, 1fr)",
																			gap: 8,
																			marginTop: message.content ? 10 : 0,
																		}}
																	>
																		{message.attachments.map((att) => (
																			<img
																				key={att.id}
																				src={att.fileUrl}
																				alt={att.fileName || "Attachment"}
																				style={{
																					width: "100%",
																					height: 120,
																					objectFit: "cover",
																					borderRadius: 8,
																				}}
																			/>
																		))}
																	</div>
																)}
															</div>
														</div>
													</div>
												</React.Fragment>
											);
										})}
										<div ref={messagesEndRef} />
									</>
								)}
							</div>

							{/* Input */}
							<div
								style={{
									padding: "16px 16px 20px",
									borderTop: "1px solid var(--color-dashboard-border)",
									background: "var(--color-dashboard-surface)",
								}}
							>
								{attachmentFiles.length > 0 && (
									<div
										style={{
											display: "flex",
											gap: 8,
											overflowX: "auto",
											paddingBottom: 10,
											marginBottom: 10,
										}}
									>
										{attachmentFiles.map((file) => (
											<div
												key={file.name}
												style={{
													display: "flex",
													alignItems: "center",
													gap: 8,
													padding: "8px 12px",
													borderRadius: 8,
													border: "1px solid var(--color-dashboard-border)",
													background: "var(--color-dashboard-divider)",
													minWidth: 100,
												}}
											>
												<Icn
													name="paperclip"
													size={14}
													color="var(--color-dashboard-muted)"
												/>
												<span
													style={{
														fontFamily: "var(--font-body)",
														fontSize: 12,
														color: "var(--color-dashboard-text)",
														overflow: "hidden",
														textOverflow: "ellipsis",
														whiteSpace: "nowrap",
													}}
												>
													{file.name}
												</span>
											</div>
										))}
									</div>
								)}
								<div
									style={{
										display: "flex",
										alignItems: "center",
										gap: 10,
										background: "var(--color-dashboard-paper)",
										border: "1px solid var(--color-dashboard-border)",
										borderRadius: 13,
										padding: "8px 8px 8px 18px",
									}}
								>
									<input
										type="text"
										value={draft}
										onChange={(e) => setDraft(e.target.value)}
										onKeyDown={handleKeyDown}
										placeholder="Write a private update…"
										style={{
											flex: 1,
											height: 40,
											border: 0,
											background: "transparent",
											outline: "none",
											fontFamily: "var(--font-body)",
											fontSize: 14,
											color: "var(--color-dashboard-text)",
										}}
									/>
									<label
										style={{
											display: "flex",
											alignItems: "center",
											gap: 6,
											padding: "8px 14px",
											borderRadius: 10,
											border: "1px solid var(--color-border-default)",
											fontFamily: "var(--font-body)",
											fontSize: 12,
											fontWeight: 500,
											color: "var(--color-dashboard-muted-dark)",
											cursor: "pointer",
											background: "var(--color-dashboard-divider)",
											boxShadow:
												"inset 0 0 0 1px color-mix(in srgb, var(--color-dashboard-paper) 50%, transparent)",
										}}
									>
										<Icn
											name="image"
											size={15}
											color="var(--color-dashboard-muted-dark)"
										/>
										{isUploading ? "Uploading…" : "Add"}
										<input
											type="file"
											accept="image/*"
											multiple
											style={{ display: "none" }}
											onChange={(e) =>
												void handleAttachmentChange(e.target.files)
											}
										/>
									</label>
									<button
										type="button"
										onClick={() => void handleSend()}
										disabled={
											(!draft.trim() && uploadedAttachments.length === 0) ||
											isSending ||
											isUploading ||
											!selectedThread?.canSend
										}
										style={{
											height: 36,
											padding: "0 18px",
											fontSize: 12.5,
											borderRadius: 8,
											fontFamily: "var(--font-body)",
											fontWeight: 600,
											display: "inline-flex",
											alignItems: "center",
											gap: 8,
											border: 0,
											cursor:
												(!draft.trim() && uploadedAttachments.length === 0) ||
												isSending ||
												isUploading ||
												!selectedThread?.canSend
													? "not-allowed"
													: "pointer",
											opacity:
												(!draft.trim() && uploadedAttachments.length === 0) ||
												isSending ||
												isUploading ||
												!selectedThread?.canSend
													? 0.5
													: 1,
											background: "var(--color-dashboard-accent)",
											color: "var(--color-dashboard-on-fill)",
											boxShadow:
												"inset 0 1px 0 rgba(255,237,213,0.35), inset 0 -1px 0 color-mix(in srgb, var(--color-dashboard-shadow-ink) 15%, transparent), 0 1px 2px color-mix(in srgb, var(--color-dashboard-shadow-ink) 18%, transparent), 0 6px 18px -6px color-mix(in srgb, var(--color-dashboard-accent) 35%, transparent), 0 12px 32px -10px color-mix(in srgb, var(--color-dashboard-accent) 30%, transparent)",
										}}
									>
										<Icn
											name="send"
											size={16}
											color="var(--color-dashboard-on-fill)"
										/>
										{isSending ? "Sending…" : "Send"}
									</button>
								</div>
								{sendError && (
									<p
										style={{
											fontFamily: "var(--font-body)",
											fontSize: 12,
											color: "var(--color-dashboard-accent-dark)",
											marginTop: 8,
										}}
									>
										{sendError}
									</p>
								)}
							</div>
						</div>
					) : (
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								justifyContent: "center",
								textAlign: "center",
								height: "100%",
								background:
									"radial-gradient(circle at top, color-mix(in srgb, var(--color-dashboard-warm-100) 50%, transparent), transparent 40%), linear-gradient(180deg, var(--color-dashboard-paper) 0%, var(--color-dashboard-surface) 100%)",
								padding: 40,
							}}
						>
							<Icn
								name="message"
								size={36}
								color="var(--color-dashboard-placeholder)"
							/>
							<h4
								style={{
									fontFamily: "var(--font-display)",
									fontStyle: "italic",
									fontWeight: 500,
									fontSize: 20,
									color: "var(--color-dashboard-text)",
									marginTop: 16,
								}}
							>
								Pick up where you left off
							</h4>
							<p
								style={{
									fontFamily: "var(--font-body)",
									fontSize: 13,
									color: "var(--color-dashboard-muted)",
									marginTop: 8,
									maxWidth: 220,
								}}
							>
								Choose a thread from the left to continue chatting
							</p>
						</div>
					)}
				</div>
			</FloatPanel>
		</div>
	);
}
